import {
  HeaderElement,
  HeaderImage,
  MediaDisplay,
  NamesDisplay,
  NavigationStyle,
  WeddingWebsiteSiteDetails,
} from "../../../resources/modelTypes";
import { HeaderMedia } from "./media/HeaderMedia";
import { css, SerializedStyles } from "@emotion/react";
import {
  CoupleHorizontalNames,
  CoupleMastheadNames,
  CouplePlacecardNames,
  CoupleStory,
  CoupleVerticalNames,
  Countdown,
  EventDate,
  EventDateHeading,
  StickyNavbar,
} from "@minted/fancyclothes";
import { styleUtils } from "@minted/minted-components";
import React, { memo, ReactNode } from "react";

const styles = {
  dateHeading: css`
    margin-bottom: ${styleUtils.rem(20)};
  `,
  nav: css`
    margin-bottom: ${styleUtils.rem(30)};
  `,
  weddingDate: (textAlignment: string) => css`
    margin-bottom: ${styleUtils.rem(30)};
    margin-top: ${styleUtils.rem(30)};
    text-align: ${textAlignment};
  `,
  // Same content gutter as LandscapeMedia / fancyclothes Wrapper.
  wrapper: css`
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(1170)};
    padding-left: ${styleUtils.rem(15)};
    padding-right: ${styleUtils.rem(15)};
  `,
};

type NavItem = {
  active: boolean;
  onClick: (event: React.MouseEvent) => void;
  title: string;
  url: string;
};

type SectionWrapperProps = {
  className: string;
  css?: SerializedStyles;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  topComponent?: ReactNode;
};

export type ModularWeddingHeaderProps = {
  children?: ReactNode;
  condensed: boolean;
  coupleNamesProps: SectionWrapperProps;
  eventDetailsWrapperProps: SectionWrapperProps;
  fallbackPhotoURL?: string | null;
  fallbackFirstPhotoURL?: string | null;
  fallbackSecondPhotoURL?: string | null;
  headerImages: HeaderImage[];
  navItems: NavItem[];
  ourStoryWrapperProps: SectionWrapperProps;
  siteDetails: WeddingWebsiteSiteDetails;
  textAlignment: string;
  weddingDate: string;
};

const renderNames = (
  headerDisplay: NamesDisplay | null | undefined,
  names: {
    bottomName: string;
    nameSeparator?: string;
    topName: string;
  },
) => {
  const { bottomName, nameSeparator, topName } = names;

  switch (headerDisplay) {
    case NamesDisplay.Horizontal:
      return (
        <CoupleHorizontalNames
          leftName={topName}
          nameSeparator={nameSeparator}
          rightName={bottomName}
        />
      );
    case NamesDisplay.Medium:
      return (
        <CoupleMastheadNames
          bottomName={bottomName}
          nameSeparator={nameSeparator}
          topName={topName}
        />
      );
    case NamesDisplay.Large:
      return (
        <CouplePlacecardNames
          bottomName={bottomName}
          nameSeparator={nameSeparator}
          topName={topName}
        />
      );
    case NamesDisplay.NamesOnly:
      return (
        <CoupleVerticalNames
          bottomName={bottomName}
          nameSeparator={nameSeparator}
          topName={topName}
        />
      );
    case NamesDisplay.None:
    default:
      return null;
  }
};

/**
 * Modular wedding header driven by siteDetails header axes
 * (headerDisplay, navigationStyle, mediaDisplay, headerElementOrder)
 * instead of the legacy themeIdentifier monoliths.
 */
const ModularWeddingHeader = ({
  children,
  condensed,
  coupleNamesProps,
  eventDetailsWrapperProps,
  fallbackPhotoURL,
  fallbackFirstPhotoURL,
  fallbackSecondPhotoURL,
  headerImages,
  navItems,
  ourStoryWrapperProps,
  siteDetails,
  textAlignment,
  weddingDate,
}: ModularWeddingHeaderProps) => {
  const topName = siteDetails.yourName || "Name";
  const bottomName = siteDetails.spouseName || "Name";
  const {
    countdownText = "the big day",
    eventDateHeading = "Please join us for our wedding celebration on",
    headerDisplay,
    headerElementOrder = [
      HeaderElement.Names,
      HeaderElement.Navigation,
      HeaderElement.Media,
    ],
    isCountdownVisible,
    isOurStoryVisible,
    isWeddingDateVisible,
    mediaDisplay: mediaDisplayValue,
    nameSeparator,
    navigationStyle = NavigationStyle.Original,
    welcomeHeading,
  } = siteDetails;

  // Default applies for undefined; ?? also covers null (destructuring defaults do not).
  const mediaDisplay = mediaDisplayValue ?? MediaDisplay.Landscape;

  // Condensed secondary pages: horizontal names + nav only (no media / date / story).
  const orderedElements = condensed
    ? [HeaderElement.Names, HeaderElement.Navigation]
    : headerElementOrder;

  const { topComponent: coupleNamesTopComponent, ...coupleNamesDivProps } =
    coupleNamesProps;
  const { topComponent: eventDetailsTopComponent, ...eventDetailsDivProps } =
    eventDetailsWrapperProps;
  const { topComponent: ourStoryTopComponent, ...ourStoryDivProps } =
    ourStoryWrapperProps;

  const introHeading = isOurStoryVisible ? welcomeHeading || "Our Story" : null;

  const showWeddingDate =
    !condensed && isWeddingDateVisible && Boolean(siteDetails.weddingDate);

  return (
    <div>
      {orderedElements.map((elementId) => {
        switch (elementId) {
          case HeaderElement.Names: {
            const namesContent = condensed
              ? renderNames(NamesDisplay.Horizontal, {
                  bottomName,
                  nameSeparator,
                  topName,
                })
              : renderNames(headerDisplay, {
                  bottomName,
                  nameSeparator,
                  topName,
                });

            if (!namesContent) {
              return null;
            }

            return (
              <div key={elementId} {...coupleNamesDivProps}>
                {coupleNamesTopComponent}
                {namesContent}
              </div>
            );
          }
          case HeaderElement.Navigation:
            return (
              <div css={styles.nav} key={elementId}>
                <StickyNavbar
                  forceScope={
                    navigationStyle === NavigationStyle.Alternate
                      ? "Top"
                      : undefined
                  }
                  items={navItems}
                />
              </div>
            );
          case HeaderElement.Media:
            return (
              <HeaderMedia
                alt={`${topName} and ${bottomName}`}
                fallbackFirstPhotoURL={fallbackFirstPhotoURL}
                fallbackPhotoURL={fallbackPhotoURL}
                fallbackSecondPhotoURL={fallbackSecondPhotoURL}
                headerImages={headerImages}
                key={elementId}
                mediaDisplay={mediaDisplay}
              />
            );
          default:
            return null;
        }
      })}

      {showWeddingDate && (
        <div css={styles.wrapper}>
          <div
            {...eventDetailsDivProps}
            css={[styles.weddingDate(textAlignment), eventDetailsDivProps.css]}
          >
            {eventDetailsTopComponent}
            <div css={styles.dateHeading}>
              <EventDateHeading>{eventDateHeading}</EventDateHeading>
            </div>
            <EventDate date={weddingDate} format="MMMM d, yyyy" />
            <Countdown
              countdownText={countdownText}
              date={weddingDate}
              isCountdownVisible={isCountdownVisible}
            />
          </div>
        </div>
      )}

      {!condensed && isOurStoryVisible && introHeading && (
        <CoupleStory
          contentTextAlignment={textAlignment}
          introHeading={introHeading}
          topComponent={ourStoryTopComponent}
          {...ourStoryDivProps}
        >
          {children}
        </CoupleStory>
      )}
    </div>
  );
};

const MemoizedModularWeddingHeader = memo(ModularWeddingHeader);

export { MemoizedModularWeddingHeader as ModularWeddingHeader };
